import React, { useState, useEffect, useRef } from 'react';
import CodingAssignmentForm from '../components/CodingAssignmentForm';
import { assignmentAPI } from '../services/api';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Select from 'react-select';

const CodingAssignments = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [showForm, setShowForm] = useState(false);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [sortBy, setSortBy] = useState(searchParams.get('sortBy') || 'createdAt');
  const [sortOrder, setSortOrder] = useState(searchParams.get('sortOrder') || 'desc');
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page')) || 1);
  const [pagination, setPagination] = useState(null);
  const [itemsPerPage, setItemsPerPage] = useState(parseInt(searchParams.get('limit')) || 10);
  const [complexityFilter, setComplexityFilter] = useState(searchParams.get('complexity') || '');
  const [developerLevelFilter, setDeveloperLevelFilter] = useState(searchParams.get('developerLevel') || '');
  
  // Cart state for batch creation
  const [assignmentCart, setAssignmentCart] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState({ current: 0, total: 0 });
  const [generationResults, setGenerationResults] = useState([]);
  const [prefillData, setPrefillData] = useState(null);
  
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const hasHandledEdit = useRef(false);

  const isAdmin = user && user.role === 'admin';

  const developerLevelLabels = {
    internDeveloper: { label: 'Intern Developer', icon: '👨‍💻', color: '#48bb78' },
    juniorDeveloper: { label: 'Junior Developer', icon: '💻', color: '#667eea' },
    midLevelDeveloper: { label: 'Mid-Level Developer', icon: '🚀', color: '#fbbf24' },
    seniorDeveloper: { label: 'Senior Developer', icon: '⭐', color: '#f97316' },
    leadDeveloper: { label: 'Lead Developer', icon: '🎖️', color: '#ef4444' }
  };

  // Update URL params whenever filter/sort/pagination changes
  useEffect(() => {
    const params = {};
    
    if (searchQuery) params.search = searchQuery;
    if (sortBy !== 'createdAt') params.sortBy = sortBy;
    if (sortOrder !== 'desc') params.sortOrder = sortOrder;
    if (currentPage !== 1) params.page = currentPage.toString();
    if (itemsPerPage !== 10) params.limit = itemsPerPage.toString();
    if (complexityFilter) params.complexity = complexityFilter;
    if (developerLevelFilter) params.developerLevel = developerLevelFilter;
    
    setSearchParams(params, { replace: true });
  }, [searchQuery, sortBy, sortOrder, currentPage, itemsPerPage, complexityFilter, developerLevelFilter]);

  useEffect(() => {
    fetchAssignments();
  }, [currentPage, sortBy, sortOrder, complexityFilter, developerLevelFilter, itemsPerPage]);

  useEffect(() => {
    // Check if navigation state contains an assignment to edit
    if (location.state?.editAssignment && !hasHandledEdit.current) {
      hasHandledEdit.current = true;
      setEditingAssignment(location.state.editAssignment);
      setShowForm(true);
      // Clear the state to prevent re-editing on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (currentPage === 1) {
        fetchAssignments();
      } else {
        setCurrentPage(1);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  const fetchAssignments = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: itemsPerPage,
        sortBy,
        sortOrder
      };

      if (searchQuery) {
        params.search = searchQuery;
      }

      if (complexityFilter) {
        params.complexity = complexityFilter;
      }

      if (developerLevelFilter) {
        params.developerLevel = developerLevelFilter;
      }

      const response = await assignmentAPI.getAllAssignments(params);
      setAssignments(response.data.assignments);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Error fetching assignments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleSortChange = (e) => {
    setSortBy(e.target.value);
  };

  const handleSortOrderToggle = () => {
    setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
  };

  const handleComplexityFilterChange = (e) => {
    setComplexityFilter(e.target.value);
    setCurrentPage(1);
  };

  const handleDeveloperLevelFilterChange = (e) => {
    setDeveloperLevelFilter(e.target.value);
    setCurrentPage(1);
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value));
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleAssignmentCreated = () => {
    setShowForm(false);
    setEditingAssignment(null);
    hasHandledEdit.current = false;
    fetchAssignments();
  };

  const handleAddToCart = (assignmentData) => {
    setAssignmentCart(prev => [...prev, assignmentData]);
  };

  const handleRemoveFromCart = (cartItemId) => {
    setAssignmentCart(prev => prev.filter(item => item.id !== cartItemId));
  };

  const handleEditFromCart = (cartItem) => {
    // Remove from cart
    setAssignmentCart(prev => prev.filter(item => item.id !== cartItem.id));
    // Set as prefill data to populate the form
    setPrefillData(cartItem);
    // Scroll to form
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleGenerateAll = async () => {
    if (assignmentCart.length === 0) return;

    setGenerating(true);
    setGenerationProgress({ current: 0, total: assignmentCart.length });
    setGenerationResults([]);
    const results = [];

    for (let i = 0; i < assignmentCart.length; i++) {
      const assignment = assignmentCart[i];
      setGenerationProgress({ current: i + 1, total: assignmentCart.length });

      try {
        const response = await assignmentAPI.createAssignment(assignment);
        results.push({
          title: assignment.title,
          success: true,
          code: response.data.assignment.assignmentCode
        });
      } catch (error) {
        results.push({
          title: assignment.title,
          success: false,
          error: error.response?.data?.message || error.message
        });
      }
    }

    setGenerationResults(results);
    setGenerating(false);
    setAssignmentCart([]);
    fetchAssignments();
  };

  if (showForm) {
    return (
      <div className="container">
        <div className="card">
          <div style={{ marginBottom: '24px' }}>
            <button 
              className="btn btn-secondary" 
              onClick={() => {
                setShowForm(false);
                setEditingAssignment(null);
                hasHandledEdit.current = false;
              }}
              style={{ fontSize: '12px', marginBottom: '16px' }}
            >
              ← Back to Assignments
            </button>
            <h2 style={{ color: '#667eea', fontSize: '22px', marginBottom: '4px' }}>
              💻 {editingAssignment ? 'Edit' : 'Create'} Coding Assignment
            </h2>
            <p style={{ color: '#718096', fontSize: '13px', margin: 0 }}>
              {editingAssignment ? 'Update assignment details and regenerate algorithm steps' : 'Build step-by-step coding assignments with AI-powered verification and hints'}
            </p>
          </div>

          {/* Cart Display */}
          {!editingAssignment && assignmentCart.length > 0 && (
            <div style={{
              backgroundColor: '#f7fafc',
              border: '2px solid #48bb78',
              borderRadius: '12px',
              padding: '20px',
              marginBottom: '24px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h3 style={{ margin: 0, color: '#48bb78', fontSize: '18px', fontWeight: '600' }}>
                  🛒 Assignment Cart ({assignmentCart.length})
                </h3>
                <button
                  onClick={handleGenerateAll}
                  disabled={generating}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: generating ? '#cbd5e0' : '#48bb78',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: generating ? 'not-allowed' : 'pointer',
                    boxShadow: generating ? 'none' : '0 2px 8px rgba(72, 187, 120, 0.3)'
                  }}
                >
                  {generating ? `🔄 Generating ${generationProgress.current}/${generationProgress.total}...` : '🚀 Generate All'}
                </button>
              </div>

              <div style={{ display: 'grid', gap: '12px' }}>
                {assignmentCart.map((item, index) => (
                  <div key={item.id} style={{
                    backgroundColor: 'white',
                    padding: '14px',
                    borderRadius: '8px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    border: '1px solid #e2e8f0'
                  }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '600', color: '#2d3748', marginBottom: '4px' }}>
                        {index + 1}. {item.title}
                      </div>
                      <div style={{ fontSize: '12px', color: '#718096' }}>
                        {item.languages.join(', ')} • {item.complexity} • {item.developerLevel}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => handleEditFromCart(item)}
                        disabled={generating}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: '#667eea',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '12px',
                          cursor: generating ? 'not-allowed' : 'pointer',
                          opacity: generating ? 0.5 : 1
                        }}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleRemoveFromCart(item.id)}
                        disabled={generating}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: '#ef4444',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '12px',
                          cursor: generating ? 'not-allowed' : 'pointer',
                          opacity: generating ? 0.5 : 1
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Generation Results */}
          {generationResults.length > 0 && (
            <div style={{
              backgroundColor: '#f7fafc',
              border: '2px solid #667eea',
              borderRadius: '12px',
              padding: '20px',
              marginBottom: '24px'
            }}>
              <h3 style={{ margin: '0 0 16px 0', color: '#667eea', fontSize: '18px', fontWeight: '600' }}>
                ✨ Generation Results
              </h3>
              <div style={{ display: 'grid', gap: '10px' }}>
                {generationResults.map((result, index) => (
                  <div key={index} style={{
                    backgroundColor: 'white',
                    padding: '12px',
                    borderRadius: '8px',
                    border: `1px solid ${result.success ? '#48bb78' : '#ef4444'}`
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '16px' }}>{result.success ? '✅' : '❌'}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: '600', color: '#2d3748', marginBottom: '2px' }}>
                          {result.title}
                        </div>
                        {result.success ? (
                          <div style={{ fontSize: '12px', color: '#48bb78' }}>
                            Created successfully! Code: <strong>{result.code}</strong>
                          </div>
                        ) : (
                          <div style={{ fontSize: '12px', color: '#ef4444' }}>
                            {result.error}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <CodingAssignmentForm 
            onSuccess={handleAssignmentCreated} 
            assignment={editingAssignment}
            onAddToCart={handleAddToCart}
            cartMode={!editingAssignment}
            prefillData={prefillData}
            onPrefillDataUsed={() => setPrefillData(null)}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="card">
        <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 style={{ color: '#667eea', fontSize: '22px', fontWeight: '700', marginBottom: '6px' }}>
              💻 Coding Assignments
            </h2>
            <p style={{ color: '#718096', fontSize: '13px' }}>
              Manage step-by-step coding assignments with AI verification
            </p>
          </div>
          <button
            className="btn btn-primary"
            onClick={() => setShowForm(true)}
            style={{ fontSize: '12px' }}
          >
            + Create New Assignment
          </button>
        </div>

        {/* Search and Filter Controls */}
        <div style={{ marginBottom: '20px', display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ flex: '1 1 300px', position: 'relative' }}>
            <input
              type="text"
              placeholder="🔍 Search assignments..."
              value={searchQuery}
              onChange={handleSearchChange}
              style={{
                width: '100%',
                padding: '10px 12px',
                fontSize: '13px',
                border: '1.5px solid #e2e8f0',
                borderRadius: '8px',
                outline: 'none',
                transition: 'all 0.2s'
              }}
              onFocus={(e) => e.target.style.borderColor = '#667eea'}
              onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
            />
          </div>

          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <Select
              value={{ value: sortBy, label: sortBy === 'createdAt' ? 'Date Created' : sortBy === 'title' ? 'Title' : sortBy === 'complexity' ? 'Complexity' : 'Developer Level' }}
              onChange={(option) => handleSortChange({ target: { value: option.value } })}
              options={[
                { value: 'createdAt', label: 'Date Created' },
                { value: 'title', label: 'Title' },
                { value: 'complexity', label: 'Complexity' },
                { value: 'developerLevel', label: 'Developer Level' }
              ]}
              styles={{
                control: (base) => ({
                  ...base,
                  minHeight: '38px',
                  minWidth: '150px',
                  fontSize: '13px',
                  borderColor: '#e2e8f0',
                  borderWidth: '1.5px'
                }),
                option: (base) => ({
                  ...base,
                  fontSize: '13px'
                })
              }}
            />

            <button
              onClick={handleSortOrderToggle}
              style={{
                padding: '10px 12px',
                fontSize: '16px',
                border: '1.5px solid #e2e8f0',
                borderRadius: '8px',
                background: 'white',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              title={sortOrder === 'asc' ? 'Ascending' : 'Descending'}
            >
              {sortOrder === 'asc' ? '↑' : '↓'}
            </button>
          </div>

          <Select
            value={complexityFilter ? { value: complexityFilter, label: complexityFilter } : { value: '', label: 'All Complexities' }}
            onChange={(option) => handleComplexityFilterChange({ target: { value: option.value } })}
            options={[
              { value: '', label: 'All Complexities' },
              { value: 'Easy', label: 'Easy' },
              { value: 'Medium', label: 'Medium' },
              { value: 'Hard', label: 'Hard' }
            ]}
            styles={{
              control: (base) => ({
                ...base,
                minHeight: '38px',
                minWidth: '150px',
                fontSize: '13px',
                borderColor: '#e2e8f0',
                borderWidth: '1.5px'
              }),
              option: (base) => ({
                ...base,
                fontSize: '13px'
              })
            }}
          />

          <Select
            value={developerLevelFilter ? { value: developerLevelFilter, label: developerLevelFilter === 'internDeveloper' ? 'Intern Developer' : developerLevelFilter === 'juniorDeveloper' ? 'Junior Developer' : developerLevelFilter === 'midLevelDeveloper' ? 'Mid-Level Developer' : developerLevelFilter === 'seniorDeveloper' ? 'Senior Developer' : 'Lead Developer' } : { value: '', label: 'All Developer Levels' }}
            onChange={(option) => handleDeveloperLevelFilterChange({ target: { value: option.value } })}
            options={[
              { value: '', label: 'All Developer Levels' },
              { value: 'internDeveloper', label: 'Intern Developer' },
              { value: 'juniorDeveloper', label: 'Junior Developer' },
              { value: 'midLevelDeveloper', label: 'Mid-Level Developer' },
              { value: 'seniorDeveloper', label: 'Senior Developer' },
              { value: 'leadDeveloper', label: 'Lead Developer' }
            ]}
            styles={{
              control: (base) => ({
                ...base,
                minHeight: '38px',
                minWidth: '180px',
                fontSize: '13px',
                borderColor: '#e2e8f0',
                borderWidth: '1.5px'
              }),
              option: (base) => ({
                ...base,
                fontSize: '13px'
              })
            }}
          />

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label style={{ fontSize: '13px', color: '#718096', whiteSpace: 'nowrap' }}>Per page:</label>
            <Select
              value={{ value: itemsPerPage, label: itemsPerPage.toString() }}
              onChange={(option) => handleItemsPerPageChange({ target: { value: option.value } })}
              options={[
                { value: 5, label: '5' },
                { value: 10, label: '10' },
                { value: 25, label: '25' },
                { value: 50, label: '50' },
                { value: 100, label: '100' }
              ]}
              styles={{
                control: (base) => ({
                  ...base,
                  minHeight: '38px',
                  minWidth: '80px',
                  fontSize: '13px',
                  borderColor: '#e2e8f0',
                  borderWidth: '1.5px'
                }),
                option: (base) => ({
                  ...base,
                  fontSize: '13px'
                })
              }}
            />
          </div>
        </div>

      {loading ? (
        <div className="loading">
          Loading assignments...
        </div>
      ) : assignments.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '48px 24px', background: '#f7fafc', borderRadius: '12px' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>
            {searchQuery || complexityFilter || developerLevelFilter ? '🔍' : '📝'}
          </div>
          <p style={{ color: '#718096', fontSize: '15px', marginBottom: '8px' }}>
            {searchQuery || complexityFilter || developerLevelFilter 
              ? 'No assignments found matching your filters' 
              : 'No coding assignments yet'}
          </p>
          <p style={{ color: '#718096', fontSize: '13px', marginBottom: '16px' }}>
            {searchQuery || complexityFilter || developerLevelFilter 
              ? 'Try adjusting your search or filters' 
              : 'Create your first coding assignment to get started'}
          </p>
          {(searchQuery || complexityFilter || developerLevelFilter) ? (
            <button
              className="btn btn-secondary"
              onClick={() => {
                setSearchQuery('');
                setComplexityFilter('');
                setDeveloperLevelFilter('');
                setCurrentPage(1);
              }}
              style={{ fontSize: '12px' }}
            >
              Clear Filters
            </button>
          ) : (
            <button
              className="btn btn-primary"
              onClick={() => setShowForm(true)}
              style={{ fontSize: '12px' }}
            >
              🚀 Create Assignment
            </button>
          )}
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '12px' }}>
          {assignments.map((assignment) => {
            const levelInfo = developerLevelLabels[assignment.developerLevel];
            return (
              <div
                key={assignment._id}
                onClick={() => {
                  if (isAdmin) {
                    navigate(`/assignment-details/${assignment._id}`);
                  } else {
                    navigate(`/coding-assignment/${assignment.assignmentCode}`);
                  }
                }}
                style={{
                  border: '1.5px solid #e2e8f0',
                  borderRadius: '12px',
                  padding: '18px',
                  background: '#fafafa',
                  transition: 'all 0.2s ease',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'white';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#fafafa';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div style={{ marginBottom: '12px' }}>
                  <h3 style={{ color: '#2d3748', fontSize: '16px', fontWeight: '600', marginBottom: '4px' }}>
                    {assignment.title}
                  </h3>
                  {assignment.description && (
                    <p style={{ color: '#718096', fontSize: '13px', marginBottom: '8px' }}>
                      {assignment.description.length > 100 
                        ? assignment.description.substring(0, 100) + '...' 
                        : assignment.description}
                    </p>
                  )}
                  <div style={{ marginBottom: '8px', display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                    <span style={{ 
                      padding: '2px 8px',
                      background: 'white',
                      color: '#667eea',
                      borderRadius: '10px',
                      fontSize: '10px',
                      fontWeight: '500',
                      border: '1px solid #c7d2fe'
                    }}>
                      📋 {assignment.assignmentCode}
                    </span>
                    <span style={{
                      padding: '2px 8px',
                      background: 'white',
                      color: assignment.complexity === 'Easy' ? '#48bb78' : 
                             assignment.complexity === 'Medium' ? '#fbbf24' : '#ef4444',
                      borderRadius: '10px',
                      fontSize: '10px',
                      fontWeight: '500',
                      border: `1px solid ${assignment.complexity === 'Easy' ? '#9ae6b4' : 
                                          assignment.complexity === 'Medium' ? '#fbd38d' : '#fc8181'}`
                    }}>
                      {assignment.complexity}
                    </span>
                    {assignment.tags && assignment.tags.length > 0 && assignment.tags.map((tag, index) => (
                      <span key={index} style={{ 
                        padding: '2px 8px',
                        background: '#f0fff4',
                        color: '#2f855a',
                        borderRadius: '10px',
                        fontSize: '10px',
                        fontWeight: '500',
                        border: '1px solid #9ae6b4'
                      }}>
                        🏷️ {tag.name || tag}
                      </span>
                    ))}
                  </div>
                  <p style={{ color: '#4a5568', fontSize: '12px' }}>
                    {levelInfo?.icon} <strong>{levelInfo?.label}</strong> • 
                    💻 <strong>{assignment.languages?.join(', ') || 'N/A'}</strong>
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination Controls */}
      {!loading && pagination && pagination.totalPages > 1 && (
        <div style={{ 
          marginTop: '24px', 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          padding: '16px',
          background: '#f7fafc',
          borderRadius: '8px'
        }}>
          <div style={{ fontSize: '13px', color: '#718096' }}>
            Showing {assignments.length} of {pagination.totalCount} assignments
            {searchQuery && ` (filtered)`}
          </div>

          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={!pagination.hasPrevPage}
              style={{
                padding: '8px 12px',
                fontSize: '13px',
                border: '1.5px solid #e2e8f0',
                borderRadius: '6px',
                background: pagination.hasPrevPage ? 'white' : '#f7fafc',
                color: pagination.hasPrevPage ? '#2d3748' : '#cbd5e0',
                cursor: pagination.hasPrevPage ? 'pointer' : 'not-allowed',
                transition: 'all 0.2s'
              }}
            >
              ← Previous
            </button>

            <div style={{ display: 'flex', gap: '4px' }}>
              {[...Array(pagination.totalPages)].map((_, index) => {
                const pageNum = index + 1;
                // Show first page, last page, current page, and pages around current
                const showPage = pageNum === 1 || 
                                pageNum === pagination.totalPages || 
                                (pageNum >= currentPage - 1 && pageNum <= currentPage + 1);
                
                if (!showPage) {
                  // Show ellipsis
                  if (pageNum === currentPage - 2 || pageNum === currentPage + 2) {
                    return <span key={pageNum} style={{ padding: '8px 4px', color: '#cbd5e0' }}>...</span>;
                  }
                  return null;
                }

                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    style={{
                      padding: '8px 12px',
                      fontSize: '13px',
                      border: '1.5px solid #e2e8f0',
                      borderRadius: '6px',
                      background: currentPage === pageNum ? '#667eea' : 'white',
                      color: currentPage === pageNum ? 'white' : '#2d3748',
                      cursor: 'pointer',
                      fontWeight: currentPage === pageNum ? '600' : '400',
                      transition: 'all 0.2s'
                    }}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={!pagination.hasNextPage}
              style={{
                padding: '8px 12px',
                fontSize: '13px',
                border: '1.5px solid #e2e8f0',
                borderRadius: '6px',
                background: pagination.hasNextPage ? 'white' : '#f7fafc',
                color: pagination.hasNextPage ? '#2d3748' : '#cbd5e0',
                cursor: pagination.hasNextPage ? 'pointer' : 'not-allowed',
                transition: 'all 0.2s'
              }}
            >
              Next →
            </button>
          </div>
        </div>
      )}

      </div>
    </div>
  );
};

export default CodingAssignments;
